/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Smarts
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
'use strict';

import Concept, { UNKNOWN_CONCEPT } from './Concept'
import conceptsJson from './resources/multi_domain_concepts.json'

/**
 * Registry to allow access to Ontology information, like concepts and their
 * properties
 *
 * @author mostafam
 *
 */
class ConceptsRegistry {

	/**
	 * constructor
	 */
	constructor() {
		this.IRI = 'IRI'
		this.NAME = 'name'
		this.CHILDREN = 'children'
		this.IS_VISIBLE = 'isVisible'
		this.ONTOLOGY_ID = 'ontologyID'
		this.PROPERTIES = 'properties'

		// registry holding mapping of IRI to concept class
		this.registry = {}
		Object
			.keys(conceptsJson)
			.forEach(domain => this.addConcept(conceptsJson[domain]));
	}

	/**
	 * utility method to create a Concept instance from its JSON rep and insert it
	 * where it belongs
	 *
	 * @param concept
	 */
	addConcept(concept) {
		// extract the basic properties
		const iri = concept[this.IRI]
		const name = concept[this.NAME]
		const isVisible = concept[this.IS_VISIBLE]
		const ontologyID = concept[this.ONTOLOGY_ID]

		// create the concept class
		const conceptObj = new Concept(iri, name)
		conceptObj.setVisible(isVisible)
		conceptObj.setOntologyID(ontologyID)

		// add properties
		const conceptProperties = concept[this.PROPERTIES]
		if (conceptProperties) {
			Object.keys(conceptProperties).forEach(property => {
				const value = conceptProperties[property]
				if (value) {
					conceptObj.addProperty(property, value)
				}
			});
		}

		// push into the registry
		this.registry[iri] = conceptObj

		// now handle the children if there is any
		if (concept.hasOwnProperty(this.CHILDREN)) {
			const children = concept[this.CHILDREN]
			children.forEach(child => {
				//Check if child concept has been encountered before
				const childObj = this.registry[child[this.IRI]] || this.addConcept(child)
				conceptObj.addChild(childObj)
			});
		}
		return conceptObj;
	}

	/**
	 * return the registered concept for this IRI
	 * @param iri the IRI of the concept
	 * @return the found concept, can be undefined
	 */
	getConcept(iri) {
		const c = this.registry[iri]
		return c && c.isVisible() ? c : UNKNOWN_CONCEPT
	}

	/**
	 * return the registered concept for each IRI
	 * @param iris the IRIs of the concepts
	 * @return the concepts
	 */
	getConcepts(iris) {
		return iris.map(iri => this.getConcept(iri))
			.filter(concept => concept!==UNKNOWN_CONCEPT)
	}
}

const conceptsRegistry = new ConceptsRegistry()
Object.freeze(conceptsRegistry)

export default conceptsRegistry
